import "./styles/index.css";
import { anchorForBlock } from "./anchors";
import { fetchCatalog, getSession, login, logout, updateComment } from "./api";
import { findComment, state } from "./app-state";
import { openCommentModal, openDeleteModal, openEditModal, openReplyModal } from "./comment-actions";
import {
  commentSkeletonHtml,
  refreshComments,
  renderBoard,
  renderDocumentComments,
  setDocumentCommentFilter,
} from "./comment-lists";
import { MOBILE_BREAKPOINT, setDocs, STORAGE } from "./constants";
import { renderIcons, ui } from "./dom";
import { goToComment, measureHeadingOffsets, openDocument, updateReadingPosition } from "./document-view";
import { bindEditorEvents, guardLeaveEditor, saveEditor, updateEditAffordances } from "./editor";
import { trapFocus } from "./focus-trap";
import { bindHistoryEvents, closeHistory, isHistoryOpen } from "./history";
import { closeModal, submitModal } from "./modal";
import { smoothScrollBehavior } from "./motion";
import { buildNavigation, readNavExpanded, writeNavExpanded } from "./navigation";
import { closePanels, openComments, openNav, updateResponsivePanels } from "./panels";
import { cycleFontScale, initFontScale } from "./reader-prefs";
import {
  composeSelectionDraft,
  getSelectionDraft,
  hideSelectionAction,
  scheduleSelectionInspection,
  showSelectionAction,
} from "./selection-ui";
import {
  clearLegacyCredentials,
  handleError,
  hardLogout,
  requireAuthor,
  setSession,
  showApp,
  showGate,
} from "./session-ui";
import { availableDocId, saveScrollPosition } from "./state";
import { showToast } from "./toast";
import { initTheme, toggleTheme } from "./theme";

async function openBoard(): Promise<void> {
  if (!(await guardLeaveEditor())) return;
  if (state.view === "document") saveScrollPosition(state, state.currentDocId, window.scrollY);
  state.view = "board";
  hideSelectionAction();
  updateEditAffordances();
  closePanels(false);
  ui.documentView.hidden = true;
  ui.boardView.hidden = false;
  ui.tocSection.hidden = true;
  ui.readingProgress.hidden = true;
  ui.wordCount.hidden = true;
  ui.commentsBtn.hidden = true;
  ui.fontScaleBtn.hidden = true;
  ui.documentsNavBtn.classList.remove("active");
  ui.boardNavBtn.classList.add("active");
  ui.viewKicker.textContent = "审阅工作台";
  ui.viewTitle.textContent = "意见汇总";
  ui.boardList.innerHTML = commentSkeletonHtml(4);
  try {
    await refreshComments();
    renderBoard();
    window.scrollTo({ top: 0, behavior: "auto" });
  } catch (error) {
    ui.boardList.innerHTML = "";
    handleError(error, "意见加载失败");
  }
}

function bindEvents(): void {
  ui.loginForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    const password = ui.password.value.trim();
    ui.loginError.textContent = "";
    if (!password) {
      ui.loginError.textContent = "请输入访问密码";
      ui.password.focus();
      return;
    }
    ui.loginBtn.disabled = true;
    ui.loginBtn.classList.add("is-loading");
    ui.loginBtn.querySelector("span")!.textContent = "正在验证";
    try {
      const session = await login(password);
      setSession(session.role);
      ui.password.value = "";
      await loadDocumentCatalog();
      showApp();
      await openDocument(state.currentDocId);
    } catch (error) {
      ui.loginError.textContent = error instanceof Error ? error.message : "登录失败";
    } finally {
      ui.loginBtn.disabled = false;
      ui.loginBtn.classList.remove("is-loading");
      ui.loginBtn.querySelector("span")!.textContent = "进入审阅";
    }
  });

  ui.logoutBtn.addEventListener("click", async () => {
    if (!(await guardLeaveEditor())) return;
    try { await logout(); } catch { /* Clearing local UI is still safe. */ }
    hardLogout("");
  });
  ui.themeBtn.addEventListener("click", toggleTheme);
  ui.fontScaleBtn.addEventListener("click", () => {
    const option = cycleFontScale();
    showToast(`正文字号：${option.label}`);
    // 字号变化改变文档高度,scrollspy 缓存需要重量。
    measureHeadingOffsets();
    updateReadingPosition();
  });
  ui.authorName.addEventListener("change", () => {
    localStorage.setItem(STORAGE.author, ui.authorName.value.trim());
  });
  ui.menuBtn.addEventListener("click", openNav);
  ui.navCloseBtn.addEventListener("click", () => closePanels());
  ui.commentsBtn.addEventListener("click", openComments);
  ui.commentsCloseBtn.addEventListener("click", () => closePanels());
  ui.pageScrim.addEventListener("click", () => closePanels());

  ui.documentsNavBtn.addEventListener("click", () => openDocument(state.currentDocId));
  ui.boardNavBtn.addEventListener("click", openBoard);
  ui.docNav.addEventListener("click", (event) => {
    const toggle = (event.target as Element).closest<HTMLButtonElement>("[data-nav-toggle]");
    if (toggle?.dataset.navToggle) {
      const key = toggle.dataset.navToggle;
      const shell = ui.docNav.querySelector(`[data-nav-key="${key}"]`);
      if (!shell) return;
      const open = !shell.classList.contains("is-open");
      shell.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", String(open));
      const expanded = readNavExpanded();
      expanded[key] = open;
      writeNavExpanded(expanded);
      return;
    }
    const button = (event.target as Element).closest<HTMLButtonElement>("button[data-doc]");
    if (button?.dataset.doc) openDocument(button.dataset.doc);
  });
  ui.tocNav.addEventListener("click", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>("button[data-heading]");
    const heading = button?.dataset.heading ? document.getElementById(button.dataset.heading) : null;
    heading?.scrollIntoView({ behavior: smoothScrollBehavior(), block: "start" });
    if (window.innerWidth <= MOBILE_BREAKPOINT) closePanels(false);
  });

  ui.statusFilters.addEventListener("click", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>("button[data-filter]");
    const filter = button?.dataset.filter;
    if (filter !== "all" && filter !== "open" && filter !== "done") return;
    state.filter = filter;
    ui.statusFilters.querySelectorAll("button").forEach((item) => item.classList.toggle("active", item === button));
    renderBoard();
  });
  ui.boardDocFilter.addEventListener("change", () => {
    state.boardDocId = ui.boardDocFilter.value;
    renderBoard();
  });
  ui.docCommentFilters.addEventListener("click", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>("button[data-doc-comment-filter]");
    const filter = button?.dataset.docCommentFilter;
    if (filter !== "open" && filter !== "all") return;
    setDocumentCommentFilter(filter);
    renderDocumentComments();
  });
  ui.newGeneralCommentBtn.addEventListener("click", () => {
    if (requireAuthor()) openCommentModal(null, "home");
  });
  ui.fullDocumentCommentBtn.addEventListener("click", () => {
    if (requireAuthor()) openCommentModal(null, state.currentDocId);
  });

  ui.reader.addEventListener("pointerup", scheduleSelectionInspection);
  ui.reader.addEventListener("touchend", scheduleSelectionInspection, { passive: true });
  ui.reader.addEventListener("click", (event) => {
    const target = event.target as Element;
    const action = target.closest<HTMLElement>("[data-action]");
    if (action?.dataset.action === "retry-doc") {
      state.markdownCache.delete(state.currentDocId);
      openDocument(state.currentDocId);
      return;
    }
    if (window.innerWidth > MOBILE_BREAKPOINT || target.closest("a, button")) return;
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) return;
    const block = target.closest<HTMLElement>("[data-review-block]");
    if (!block || !ui.reader.contains(block)) return;
    const anchor = anchorForBlock(block);
    if (anchor) showSelectionAction(anchor);
  });
  ui.selectionCommentBtn.addEventListener("pointerdown", () => {
    composeSelectionDraft();
  });
  ui.selectionCommentBtn.addEventListener("click", () => {
    const anchor = getSelectionDraft().anchor || state.quote;
    if (!anchor) return;
    composeSelectionDraft();
    if (requireAuthor()) openCommentModal(anchor, state.currentDocId);
  });
  document.addEventListener("pointerdown", (event) => {
    const target = event.target as Node;
    if (getSelectionDraft().phase === "composing") return;
    if (!ui.selectionAction.contains(target) && !ui.reader.contains(target)) hideSelectionAction();
  });
  document.addEventListener("selectionchange", () => {
    if (getSelectionDraft().phase === "composing") return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      hideSelectionAction();
      return;
    }
    scheduleSelectionInspection();
  });
  document.addEventListener("keyup", (event) => {
    if (event.shiftKey || event.key.startsWith("Arrow")) scheduleSelectionInspection();
  });

  ui.modalCloseBtn.addEventListener("click", () => closeModal());
  ui.modalCancelBtn.addEventListener("click", () => closeModal());
  ui.modalMask.addEventListener("click", (event) => {
    if (event.target === ui.modalMask) closeModal();
  });
  ui.modalSubmitBtn.addEventListener("click", submitModal);

  bindEditorEvents();
  bindHistoryEvents();

  document.addEventListener("click", async (event) => {
    const action = (event.target as Element).closest<HTMLElement>("[data-action]");
    if (!action || ui.reader.contains(action)) return;
    const id = action.dataset.id;
    const comment = id ? findComment(id) : undefined;
    try {
      if (action.dataset.action === "reply" && comment && state.session.isAdmin) {
        openReplyModal(comment);
      } else if (action.dataset.action === "edit" && comment && (state.session.isAdmin || comment.owned_by_me)) {
        openEditModal(comment);
      } else if (action.dataset.action === "delete" && comment && (state.session.isAdmin || comment.owned_by_me)) {
        openDeleteModal(comment);
      } else if (action.dataset.action === "toggle-status" && comment && state.session.isAdmin) {
        const status = comment.status === "done" ? "open" : "done";
        await updateComment(comment.id, { status });
        await refreshComments();
        showToast(status === "done" ? "已标记为处理完成" : "意见已重新打开", "success");
      } else if (action.dataset.action === "quote" && comment) {
        await goToComment(comment);
      } else if (action.dataset.action === "goto-doc" && action.dataset.doc) {
        await openDocument(action.dataset.doc, false);
      }
    } catch (error) {
      handleError(error, "操作失败");
    }
  });

  document.addEventListener("keydown", (event) => {
    if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "s") {
      if (state.editor) {
        event.preventDefault();
        void saveEditor();
      }
      return;
    }
    if (event.key === "Escape") {
      if (!ui.modalMask.hidden) closeModal();
      else if (isHistoryOpen()) closeHistory();
      else if (document.body.classList.contains("panel-open")) closePanels();
      else hideSelectionAction();
      return;
    }
    if (!ui.modalMask.hidden) trapFocus(event, ui.modal);
    else if (isHistoryOpen()) trapFocus(event, ui.historyDialog);
    else if (document.body.classList.contains("nav-open")) trapFocus(event, ui.sidebar);
    else if (document.body.classList.contains("comments-open")) trapFocus(event, ui.commentsPanel);
  });

  window.addEventListener("scroll", () => {
    updateReadingPosition();
    const phase = getSelectionDraft().phase;
    if (phase !== "composing" && phase !== "idle") {
      hideSelectionAction();
    }
  }, { passive: true });
  window.addEventListener("resize", () => {
    updateResponsivePanels();
    measureHeadingOffsets();
    if (getSelectionDraft().phase !== "composing") hideSelectionAction();
  });
}

async function loadDocumentCatalog(): Promise<void> {
  try {
    const docs = await fetchCatalog();
    if (docs.length) {
      setDocs(docs);
      state.currentDocId = availableDocId(localStorage.getItem(STORAGE.currentDoc));
    }
  } catch (error) {
    console.warn("catalog load failed, using seed docs", error);
    showToast("在线目录暂时不可用，正在使用本地目录", "error");
  }
  buildNavigation();
  renderIcons();
}

async function boot(): Promise<void> {
  clearLegacyCredentials();
  initTheme();
  initFontScale();
  ui.authorName.value = localStorage.getItem(STORAGE.author) || "";
  buildNavigation();
  bindEvents();
  renderIcons();
  updateResponsivePanels();

  try {
    const session = await getSession();
    if (!session.ok || !session.role) {
      showGate();
      return;
    }
    await loadDocumentCatalog();
    setSession(session.role);
    showApp();
    await openDocument(state.currentDocId);
  } catch {
    showGate("暂时无法连接服务器，请稍后重试");
  }
}

void boot();
